import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Store, Users, CalendarCheck, DollarSign, MapPin, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { api, fmtErr, API } from "../lib/api";

const STATUS_STYLES = {
  approved: "bg-emerald-400/10 text-emerald-300 border-emerald-400/30",
  pending: "bg-amber-400/10 text-amber-300 border-amber-400/30",
  suspended: "bg-red-400/10 text-red-300 border-red-400/30",
};

function SalesCodesCard() {
  const [codes, setCodes] = useState([]);
  useEffect(() => {
    api.get("/admin/sales-codes").then((r) => setCodes(r.data)).catch(() => {});
  }, []);
  if (codes.length === 0) return null;
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-5 mb-6" data-testid="sales-codes-card">
      <h3 className="font-outfit font-medium text-base mb-3">Signups by sales code</h3>
      <div className="flex flex-wrap gap-3">
        {codes.map((c) => (
          <div key={c.code} data-testid={`sales-code-stat-${c.code}`}
            className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
            <p className="text-sm font-medium text-primary">{c.code}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {c.signups} signup{c.signups === 1 ? "" : "s"} · {c.this_month} this month
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SalesTab() {
  const [reps, setReps] = useState([]);
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({ name: "", code: "", target: "" });

  const load = () => {
    api.get("/admin/sales-reps").then((r) => setReps(r.data)).catch(() => {});
    api.get("/admin/sales-codes").then((r) => setCodes(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const addRep = async () => {
    try {
      await api.post("/admin/sales-reps", { ...form, monthly_target: Number(form.target) || 0 });
      toast.success(`${form.name} added with code`);
      setForm({ name: "", code: "", target: "" });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const toggleRep = async (rep) => {
    try {
      await api.patch(`/admin/sales-reps/${rep.id}`, { active: !rep.active });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const removeRep = async (rep) => {
    try {
      await api.delete(`/admin/sales-reps/${rep.id}`);
      toast.success(`${rep.name} removed`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="sales-tab">
      <div className="lg:col-span-2 rounded-2xl border border-border/60 bg-card p-6">
        <h3 className="font-outfit font-medium text-lg">Sales team</h3>
        <p className="text-xs text-muted-foreground mt-1">Each rep gets a code they share with prospects. Signups using it are attributed automatically.</p>
        <div className="flex gap-2 mt-4">
          <Input data-testid="rep-name-input" placeholder="Rep name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-background border-border/60" />
          <Input data-testid="rep-code-input" placeholder="CODE" value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            className="bg-background border-border/60 w-32 font-mono" />
          <Input data-testid="rep-target-input" placeholder="Target/mo" type="number" min="0" value={form.target}
            onChange={(e) => setForm({ ...form, target: e.target.value })}
            className="bg-background border-border/60 w-28" />
          <Button data-testid="add-rep-btn" size="sm" className="rounded-full shrink-0" onClick={addRep}
            disabled={!form.name.trim() || form.code.trim().length < 3}>Add</Button>
        </div>
        <div className="mt-4 space-y-2">
          {reps.map((r) => (
            <div key={r.id} data-testid={`rep-row-${r.code}`}
              className={`flex items-center justify-between rounded-xl border px-4 py-2.5 ${r.active ? "border-border/40" : "border-border/30 opacity-60"}`}>
              <div>
                <p className="text-sm font-medium">{r.name}</p>
                <p className="text-xs text-muted-foreground font-mono">{r.code}{r.monthly_target ? ` · target ${r.monthly_target}/mo` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button data-testid={`rep-link-${r.code}`} size="sm" variant="outline" className="rounded-full h-8 text-xs"
                  onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/register?role=owner&code=${r.code}`); toast.success("Signup link copied"); }}>
                  Copy link
                </Button>
                <Switch data-testid={`rep-active-${r.code}`} checked={r.active} onCheckedChange={() => toggleRep(r)} />
                <Button data-testid={`rep-delete-${r.code}`} size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-full text-destructive"
                  onClick={() => removeRep(r)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
          {reps.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-reps-msg">No sales reps yet.</p>}
        </div>
      </div>

      <div className="lg:col-span-3 rounded-2xl border border-border/60 bg-card p-6">
        <h3 className="font-outfit font-medium text-lg">Conversions by code</h3>
        <p className="text-xs text-muted-foreground mt-1">Signup → venue created → approved → paying.</p>
        {reps.length > 0 && (
          <div className="mt-4 space-y-2.5" data-testid="rep-leaderboard">
            {[...reps].sort((a, b) => {
              const ca = codes.find((c) => c.code === a.code)?.this_month || 0;
              const cb = codes.find((c) => c.code === b.code)?.this_month || 0;
              return cb - ca;
            }).map((rep, i) => {
              const conv = codes.find((c) => c.code === rep.code) || { this_month: 0 };
              const pct = rep.monthly_target ? Math.min(100, Math.round((conv.this_month / rep.monthly_target) * 100)) : 0;
              return (
                <div key={rep.id} data-testid={`leader-${rep.code}`} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-4">{i + 1}.</span>
                  <span className="text-sm w-28 truncate">{rep.name}</span>
                  <div className="flex-1 h-2 rounded-full bg-border/40 overflow-hidden">
                    <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {conv.this_month}{rep.monthly_target ? ` / ${rep.monthly_target}` : ""} this month
                  </span>
                </div>
              );
            })}
          </div>
        )}
        <Table className="mt-4">
          <TableHeader>
            <TableRow className="border-border/60">
              <TableHead>Code</TableHead><TableHead>Rep</TableHead><TableHead>Signups</TableHead>
              <TableHead>This month</TableHead><TableHead>Venues</TableHead><TableHead>Approved</TableHead><TableHead>Paying</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {codes.map((c) => (
              <TableRow key={c.code} data-testid={`conv-row-${c.code}`} className="border-border/40">
                <TableCell className="font-mono text-primary">{c.code}</TableCell>
                <TableCell className="text-muted-foreground">{c.rep_name || "—"}</TableCell>
                <TableCell>{c.signups}</TableCell>
                <TableCell>{c.this_month}</TableCell>
                <TableCell>{c.venues}</TableCell>
                <TableCell>{c.approved}</TableCell>
                <TableCell className={c.paying ? "text-primary font-medium" : ""}>{c.paying}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {codes.length === 0 && <p className="text-sm text-muted-foreground mt-4" data-testid="no-conversions-msg">No coded signups yet — share a rep's code to start tracking.</p>}
      </div>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [content, setContent] = useState(null);
  const [site, setSite] = useState(null);
  const [tour, setTour] = useState(null);
  const [cmsUploading, setCmsUploading] = useState("");

  const uploadAdminMedia = async (kind, file, done) => {
    const fd = new FormData();
    fd.append("file", file);
    setCmsUploading(kind);
    try {
      const { data } = await api.post(`/admin/media?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      done(`${API}${data.url}`);
      toast.success("Image uploaded — publish changes to go live");
    } catch (e) {
      toast.error(fmtErr(e));
    }
    setCmsUploading("");
  };
  const [plans, setPlans] = useState(null);
  const [planEdits, setPlanEdits] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [adminForm, setAdminForm] = useState({ name: "", email: "", password: "" });
  const [integrations, setIntegrations] = useState(null);
  const [stripeCfg, setStripeCfg] = useState(null);
  const [stripeForm, setStripeForm] = useState({ publishable_key: "", secret_key: "", webhook_secret: "" });
  const [smtpForm, setSmtpForm] = useState({ host: "", port: 587, username: "", password: "", from_email: "" });
  const [twilioForm, setTwilioForm] = useState({ account_sid: "", auth_token: "", from_number: "", enabled: false });
  const [gvForm, setGvForm] = useState({ base_url: "", api_key: "", enabled: false });
  const [vendorFilter, setVendorFilter] = useState("all");
  const [deletingVendor, setDeletingVendor] = useState(null);

  const deleteRestaurant = async () => {
    try {
      await api.delete(`/admin/restaurants/${deletingVendor.id}`);
      toast.success(`${deletingVendor.name} deleted`);
      setDeletingVendor(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const saveIntegrations = async (kind) => {
    try {
      const payload = kind === "smtp" ? { smtp: smtpForm } : kind === "twilio" ? { twilio: twilioForm } : { giftvouchers: gvForm };
      await api.put("/admin/integrations", payload);
      toast.success(kind === "smtp" ? "SMTP settings saved" : kind === "twilio" ? "Twilio settings saved" : "GiftVouchers.app settings saved");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const testSmtp = async () => {
    try {
      await api.post("/admin/integrations/smtp-test", {});
      toast.success("Test email sent — check your inbox");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const toggleBilling = async (r) => {
    try {
      const enabling = r.billing_enabled === false;
      await api.post(`/admin/restaurants/${r.id}/billing`, { enabled: enabling });
      toast.success(enabling ? "Invoicing enabled" : "Invoicing disabled — fees waived for this client");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const addAdmin = async () => {
    try {
      await api.post("/admin/admins", adminForm);
      toast.success(`${adminForm.email} is now a super-admin`);
      setAdminForm({ name: "", email: "", password: "" });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const removeAdmin = async (email) => {
    try {
      await api.delete(`/admin/admins/${encodeURIComponent(email)}`);
      toast.success("Admin removed");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  const [locations, setLocations] = useState([]);
  const [locForm, setLocForm] = useState({ name: "", country: "" });
  const [customers, setCustomers] = useState([]);
  const [customerEmail, setCustomerEmail] = useState(null);
  const [customerBookings, setCustomerBookings] = useState([]);

  const addLocation = async () => {
    try {
      await api.post("/admin/locations", locForm);
      toast.success("Location added");
      setLocForm({ name: "", country: "" });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const toggleLocation = async (loc) => {
    try {
      await api.put(`/admin/locations/${loc.id}`, { active: !loc.active });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const deleteLocation = async (lid) => {
    try {
      await api.delete(`/admin/locations/${lid}`);
      toast.success("Location removed");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const viewCustomer = (email) => {
    setCustomerEmail(email);
    api.get(`/admin/customers/${encodeURIComponent(email)}/bookings`).then((r) => setCustomerBookings(r.data)).catch(() => {});
  };

  const overrideBooking = async (b, status) => {
    try {
      await api.patch(`/restaurants/${b.restaurant_id}/bookings/${b.id}`, { status });
      toast.success("Booking updated");
      viewCustomer(customerEmail);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const impersonate = async (rid) => {
    try {
      await api.post(`/admin/restaurants/${rid}/impersonate`);
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const setTier = async (rid, tier) => {
    try {
      await api.post(`/admin/restaurants/${rid}/tier`, { tier });
      toast.success(`Plan set to ${tier}`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const waive = async (rid) => {
    try {
      await api.post(`/admin/restaurants/${rid}/waive`, {});
      toast.success("Outstanding fees waived");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const savePlan = async (tier) => {
    const p = planEdits[tier];
    try {
      await api.put(`/admin/plans/${tier}`, {
        price: Number(p.price), commission: Number(p.commission),
        tables: Number(p.tables), staff: Number(p.staff), sms: !!p.sms,
        features: Array.isArray(p.features) ? p.features : undefined,
      });
      toast.success(`${tier} plan updated`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const saveContent = async () => {
    try {
      if (content) await api.put("/admin/content", { key: "landing", value: content });
      if (site) await api.put("/admin/content", { key: "site", value: site });
      if (tour) await api.put("/admin/content", { key: "tour", value: tour });
      toast.success("Site content published");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const saveStripeCfg = async () => {
    try {
      await api.put("/admin/stripe-config", stripeForm);
      toast.success("Stripe live keys saved — new payments use them immediately");
      setStripeForm({ publishable_key: stripeForm.publishable_key, secret_key: "", webhook_secret: "" });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const clearStripeCfg = async () => {
    try {
      await api.put("/admin/stripe-config", { clear: true });
      toast.success("Live keys removed — back to sandbox (test) keys");
      setStripeForm({ publishable_key: "", secret_key: "", webhook_secret: "" });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/restaurants").then((r) => setRestaurants(r.data)).catch(() => {});
    api.get("/admin/commissions").then((r) => setCommissions(r.data)).catch(() => {});
    api.get("/content").then((r) => { setContent(r.data.landing); setSite(r.data.site); setTour(r.data.tour); }).catch(() => {});
    api.get("/plans").then((r) => { setPlans(r.data); setPlanEdits(JSON.parse(JSON.stringify(r.data))); }).catch(() => {});
    api.get("/admin/invoices").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/admin/locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/admin/admins").then((r) => setAdmins(r.data)).catch(() => {});
    api.get("/admin/integrations").then((r) => {
      setIntegrations(r.data);
      setSmtpForm((f) => ({ ...f, host: r.data.smtp.host, port: r.data.smtp.port, username: r.data.smtp.username, from_email: r.data.smtp.from_email }));
      setTwilioForm((f) => ({ ...f, account_sid: r.data.twilio.account_sid, from_number: r.data.twilio.from_number, enabled: r.data.twilio.enabled }));
      setGvForm((f) => ({ ...f, base_url: r.data.giftvouchers?.base_url || "", enabled: r.data.giftvouchers?.enabled || false }));
    }).catch(() => {});
    api.get("/admin/stripe-config").then((r) => {
      setStripeCfg(r.data);
      setStripeForm((f) => ({ ...f, publishable_key: r.data.publishable_key || "" }));
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (rid, status) => {
    try {
      await api.post(`/admin/restaurants/${rid}/status`, { status });
      toast.success(`Restaurant ${status}`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const toggleListed = async (r) => {
    try {
      const listing = r.listed === false;
      await api.post(`/admin/restaurants/${r.id}/listed`, { listed: listing });
      toast.success(listing ? `${r.name} shown on marketplace` : `${r.name} hidden from marketplace`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const filteredVendors = vendorFilter === "all" ? restaurants : restaurants.filter((r) => r.status === vendorFilter);

  const cards = stats ? [
    { icon: Store, label: "Restaurants", value: stats.restaurants, testid: "admin-stat-restaurants" },
    { icon: Users, label: "Users", value: stats.users, testid: "admin-stat-users" },
    { icon: CalendarCheck, label: "Active bookings", value: stats.active_bookings, testid: "admin-stat-bookings" },
    { icon: DollarSign, label: "Commission accrued", value: `£${stats.commission_total.toFixed(2)}`, testid: "admin-stat-commission" },
  ] : [];

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12 min-h-screen">
      <p className="text-xs tracking-[0.3em] uppercase text-primary">Platform</p>
      <h1 className="font-outfit font-semibold text-3xl tracking-tight mt-1">Admin console</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid}
            className="rounded-2xl border border-border/60 bg-card p-5 hover:-translate-y-[1px] transition-transform duration-200">
            <div className="flex items-center justify-between">
              <p className="text-xs tracking-[0.15em] uppercase text-muted-foreground">{c.label}</p>
              <c.icon className="w-4 h-4 text-primary" />
            </div>
            <p className="font-outfit font-semibold text-3xl mt-3">{c.value}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="vendors" className="mt-10">
        <TabsList data-testid="admin-tabs" className="bg-card border border-border/60 w-full max-w-full overflow-x-auto flex-nowrap justify-start">
          <TabsTrigger data-testid="admin-tab-vendors" value="vendors">Vendors</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-commissions" value="commissions">Commissions</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-plans" value="plans">Plans</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-invoices" value="invoices">Invoices</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-locations" value="locations">Locations</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-sales" value="sales">Sales</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-customers" value="customers">Customers</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-admins" value="admins">Admins</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-integrations" value="integrations">Integrations</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-cms" value="cms">CMS</TabsTrigger>
        </TabsList>
        <TabsContent value="vendors" className="mt-6">
          <SalesCodesCard />
          <div className="flex gap-2 mb-4 flex-wrap">
            {[["all", "All"], ["pending", "Pending"], ["approved", "Approved"], ["suspended", "Suspended"]].map(([v, label]) => (
              <button key={v} data-testid={`vendor-filter-${v}`} onClick={() => setVendorFilter(v)}
                className={`px-4 py-1.5 rounded-full text-xs border transition-colors ${vendorFilter === v ? "bg-primary text-primary-foreground border-primary" : "border-border/60 text-muted-foreground hover:text-foreground"}`}>
                {label} ({v === "all" ? restaurants.length : restaurants.filter((r) => r.status === v).length})
              </button>
            ))}
          </div>
          <div className="rounded-2xl border border-border/60 bg-card overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/60">
                  <TableHead>Restaurant</TableHead>
                  <TableHead>Cuisine</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVendors.map((r) => (
                  <TableRow key={r.id} data-testid={`vendor-row-${r.id}`} className="border-border/40">
                    <TableCell className="font-medium">
                      {r.name}
                      {r.sales_code && (
                        <span data-testid={`sales-code-${r.id}`} className="ml-2 inline-block text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/30">
                          {r.sales_code}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{r.cuisine}</TableCell>
                    <TableCell className="text-sm">{r.city}</TableCell>
                    <TableCell>
                      <Select value={r.subscription_tier} onValueChange={(v) => setTier(r.id, v)}>
                        <SelectTrigger data-testid={`tier-select-${r.id}`} className="w-28 h-8 bg-background border-border/60 text-xs capitalize">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="free">Free</SelectItem>
                          <SelectItem value="starter">Starter</SelectItem>
                          <SelectItem value="pro">Pro</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_STYLES[r.status]}>{r.status}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button data-testid={`impersonate-${r.id}`} size="sm" variant="secondary" className="rounded-full h-8"
                          onClick={() => impersonate(r.id)}>Log in</Button>
                        <Button data-testid={`billing-toggle-${r.id}`} size="sm" variant="outline"
                          className={`rounded-full h-8 ${r.billing_enabled === false ? "text-amber-500 border-amber-500/40" : ""}`}
                          onClick={() => toggleBilling(r)}>
                          {r.billing_enabled === false ? "Fees off" : "Fees on"}
                        </Button>
                        <Button data-testid={`listed-toggle-${r.id}`} size="sm" variant="outline"
                          className={`rounded-full h-8 ${r.listed === false ? "text-amber-500 border-amber-500/40" : ""}`}
                          onClick={() => toggleListed(r)}>
                          {r.listed === false ? "Unlisted" : "Listed"}
                        </Button>
                        {r.status !== "approved" && (
                          <Button data-testid={`approve-${r.id}`} size="sm" variant="outline" className="rounded-full h-8"
                            onClick={() => setStatus(r.id, "approved")}>Approve</Button>
                        )}
                        {r.status !== "suspended" && (
                          <Button data-testid={`suspend-${r.id}`} size="sm" variant="ghost" className="rounded-full h-8 text-destructive"
                            onClick={() => setStatus(r.id, "suspended")}>Suspend</Button>
                        )}
                        <Button data-testid={`delete-${r.id}`} size="sm" variant="ghost" className="rounded-full h-8 text-destructive"
                          onClick={() => setDeletingVendor(r)} aria-label={`Delete ${r.name}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredVendors.length === 0 && (
                  <TableRow className="border-border/40">
                    <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8" data-testid="vendors-empty">
                      No businesses in this view.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <Dialog open={!!deletingVendor} onOpenChange={() => setDeletingVendor(null)}>
          <DialogContent className="bg-card border-border/60">
            {deletingVendor && (
              <>
                <DialogHeader><DialogTitle className="font-outfit">Delete {deletingVendor.name}?</DialogTitle></DialogHeader>
                <p className="text-sm text-muted-foreground mt-2" data-testid="vendor-delete-warning">
                  This permanently removes the business and all its data — tables, bookings, menus, orders, inventory,
                  events, staff accounts and the owner login. Financial records (invoices, payments) are kept.
                  This cannot be undone.
                </p>
                <div className="flex gap-3 mt-6">
                  <Button data-testid="vendor-delete-cancel" variant="outline" className="rounded-full flex-1"
                    onClick={() => setDeletingVendor(null)}>Cancel</Button>
                  <Button data-testid="vendor-delete-confirm" variant="destructive" className="rounded-full flex-1"
                    onClick={deleteRestaurant}>Delete permanently</Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
        <TabsContent value="commissions" className="mt-6">
          <div className="rounded-2xl border border-border/60 bg-card overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/60">
                  <TableHead>Restaurant</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Bookings</TableHead>
                  <TableHead>Outstanding fees</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {commissions.map((c) => (
                  <TableRow key={c.restaurant_id} data-testid={`commission-row-${c.restaurant_id}`} className="border-border/40">
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="capitalize text-sm">{c.tier}</TableCell>
                    <TableCell>{c.bookings}</TableCell>
                    <TableCell className="font-outfit font-medium">£{c.commission_balance.toFixed(2)}</TableCell>
                    <TableCell>
                      {c.commission_balance > 0 && (
                        <Button data-testid={`waive-${c.restaurant_id}`} size="sm" variant="ghost" className="rounded-full h-8 text-primary"
                          onClick={() => waive(c.restaurant_id)}>Waive</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="plans" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="plans-panel">
            {plans && Object.entries(plans).map(([tier, p]) => (
              <div key={tier} className="rounded-2xl border border-border/60 bg-card p-6" data-testid={`plan-edit-${tier}`}>
                <h3 className="font-outfit font-medium text-lg capitalize">{p.name}</h3>
                <div className="mt-4 space-y-3">
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Monthly rate (£)</Label>
                    <Input data-testid={`plan-price-${tier}`} type="number" min="0" value={planEdits[tier]?.price ?? p.price}
                      onChange={(e) => setPlanEdits({ ...planEdits, [tier]: { ...planEdits[tier], price: e.target.value } })}
                      className="mt-1.5 bg-background border-border/60" />
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Per-booking fee (£)</Label>
                    <Input data-testid={`plan-commission-${tier}`} type="number" min="0" step="0.05" value={planEdits[tier]?.commission ?? p.commission}
                      onChange={(e) => setPlanEdits({ ...planEdits, [tier]: { ...planEdits[tier], commission: e.target.value } })}
                      className="mt-1.5 bg-background border-border/60" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Tables</Label>
                      <Input data-testid={`plan-tables-${tier}`} type="number" min="1" value={planEdits[tier]?.tables ?? p.tables}
                        onChange={(e) => setPlanEdits({ ...planEdits, [tier]: { ...planEdits[tier], tables: e.target.value } })}
                        className="mt-1.5 bg-background border-border/60" />
                    </div>
                    <div>
                      <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Staff seats</Label>
                      <Input data-testid={`plan-staff-${tier}`} type="number" min="0" value={planEdits[tier]?.staff ?? p.staff}
                        onChange={(e) => setPlanEdits({ ...planEdits, [tier]: { ...planEdits[tier], staff: e.target.value } })}
                        className="mt-1.5 bg-background border-border/60" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Feature bullets (one per line — shown on the homepage pricing)</Label>
                    <Textarea data-testid={`plan-features-${tier}`} rows={4}
                      value={(planEdits[tier]?.features ?? p.features ?? []).join("\n")}
                      onChange={(e) => setPlanEdits({ ...planEdits, [tier]: { ...planEdits[tier], features: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) } })}
                      className="mt-1.5 bg-background border-border/60" />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">SMS notifications (Twilio)</Label>
                    <Switch data-testid={`plan-sms-${tier}`} checked={!!(planEdits[tier]?.sms ?? p.sms)}
                      onCheckedChange={(v) => setPlanEdits({ ...planEdits, [tier]: { ...planEdits[tier], sms: v } })} />
                  </div>
                  <Button data-testid={`plan-save-${tier}`} onClick={() => savePlan(tier)} className="w-full rounded-full" size="sm">
                    Save {tier} plan
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4">Note: monthly rate changes here update feature limits and display pricing. Existing Stripe subscription prices stay as originally billed.</p>
        </TabsContent>
        <TabsContent value="invoices" className="mt-6">
          <div className="rounded-2xl border border-border/60 bg-card overflow-x-auto" data-testid="invoices-panel">
            <Table>
              <TableHeader>
                <TableRow className="border-border/60">
                  <TableHead>Restaurant</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Bookings</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border-border/40">
                    <TableCell className="font-medium">{inv.restaurant_name}</TableCell>
                    <TableCell>{inv.period}</TableCell>
                    <TableCell>{inv.bookings}</TableCell>
                    <TableCell className="font-outfit font-medium">£{inv.amount.toFixed(2)}</TableCell>
                    <TableCell><Badge variant="outline" className="text-emerald-300 border-emerald-400/30">{inv.status}</Badge></TableCell>
                  </TableRow>
                ))}
                {invoices.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground" data-testid="no-invoices-msg">
                    No invoices yet — they're generated automatically on the 1st of each month.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="locations" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="locations-panel">
            <div className="rounded-2xl border border-border/60 bg-card p-6">
              <h3 className="font-outfit font-medium text-lg flex items-center gap-2"><MapPin className="w-5 h-5 text-primary" /> Add location</h3>
              <p className="text-xs text-muted-foreground mt-1">Each city gets a shareable link: /c/slug</p>
              <div className="mt-5 space-y-3">
                <Input data-testid="location-name-input" placeholder="City name (e.g. Edinburgh)" value={locForm.name}
                  onChange={(e) => setLocForm({ ...locForm, name: e.target.value })} className="bg-background border-border/60" />
                <Input data-testid="location-country-input" placeholder="Country (optional)" value={locForm.country}
                  onChange={(e) => setLocForm({ ...locForm, country: e.target.value })} className="bg-background border-border/60" />
                <Button data-testid="location-add-btn" onClick={addLocation} disabled={!locForm.name} className="w-full rounded-full">
                  Add location
                </Button>
              </div>
            </div>
            <div className="lg:col-span-2 rounded-2xl border border-border/60 bg-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/60">
                    <TableHead>City</TableHead>
                    <TableHead>Share link</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {locations.map((l) => (
                    <TableRow key={l.id} data-testid={`location-row-${l.id}`} className="border-border/40">
                      <TableCell className="font-medium">{l.name}{l.country ? `, ${l.country}` : ""}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">/c/{l.slug}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={l.active ? "text-emerald-500 border-emerald-500/30" : "text-muted-foreground"}>
                          {l.active ? "active" : "hidden"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button data-testid={`toggle-location-${l.id}`} size="sm" variant="outline" className="rounded-full h-8"
                            onClick={() => toggleLocation(l)}>{l.active ? "Hide" : "Show"}</Button>
                          <Button data-testid={`delete-location-${l.id}`} size="sm" variant="ghost" className="rounded-full h-8 text-destructive"
                            onClick={() => deleteLocation(l.id)}>Delete</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {locations.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-center py-12 text-muted-foreground" data-testid="no-locations-msg">
                      No locations yet — add your first city.
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="sales" className="mt-6">
          <SalesTab />
        </TabsContent>
        <TabsContent value="customers" className="mt-6">
          <div className="rounded-2xl border border-border/60 bg-card overflow-x-auto" data-testid="customers-panel">
            <Table>
              <TableHeader>
                <TableRow className="border-border/60">
                  <TableHead>Customer</TableHead>
                  <TableHead>Bookings</TableHead>
                  <TableHead>Last booking</TableHead>
                  <TableHead>Restaurants</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((c) => (
                  <TableRow key={c.email} data-testid={`customer-row-${c.email}`} className="border-border/40">
                    <TableCell>
                      <p className="font-medium">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.email}</p>
                    </TableCell>
                    <TableCell>{c.bookings}</TableCell>
                    <TableCell className="text-sm">{c.last_booking}</TableCell>
                    <TableCell className="text-sm max-w-[200px] truncate">{c.restaurants.join(", ")}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={c.registered ? "text-emerald-500 border-emerald-500/30" : "text-muted-foreground"}>
                        {c.registered ? "registered" : "guest"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button data-testid={`view-customer-${c.email}`} size="sm" variant="outline" className="rounded-full h-8"
                        onClick={() => viewCustomer(c.email)}>View bookings</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {customers.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground" data-testid="no-customers-msg">
                    No customers yet.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="admins" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="admins-panel">
            <div className="rounded-2xl border border-border/60 bg-card p-6">
              <h3 className="font-outfit font-medium text-lg">Add super-admin</h3>
              <p className="text-xs text-muted-foreground mt-1">Super-admins get full platform access: vendors, customers, plans, CMS and overrides.</p>
              <div className="mt-5 space-y-3">
                <Input data-testid="admin-name-input" placeholder="Full name" value={adminForm.name}
                  onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })} className="bg-background border-border/60" />
                <Input data-testid="admin-email-input" type="email" placeholder="Email" value={adminForm.email}
                  onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })} className="bg-background border-border/60" />
                <Input data-testid="admin-password-input" type="password" placeholder="Temporary password (min 8 chars)" value={adminForm.password}
                  onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} className="bg-background border-border/60" />
                <Button data-testid="admin-add-btn" onClick={addAdmin}
                  disabled={!adminForm.name || !adminForm.email || adminForm.password.length < 8} className="w-full rounded-full">
                  Add super-admin
                </Button>
              </div>
            </div>
            <div className="rounded-2xl border border-border/60 bg-card p-6">
              <h3 className="font-outfit font-medium text-lg">Current admins</h3>
              <div className="mt-4 space-y-3">
                {admins.map((a) => (
                  <div key={a.email} data-testid={`admin-row-${a.email}`}
                    className="flex items-center justify-between rounded-xl border border-border/40 px-4 py-3">
                    <div>
                      <p className="text-sm font-medium">{a.name}</p>
                      <p className="text-xs text-muted-foreground">{a.email}</p>
                    </div>
                    {admins.length > 1 && (
                      <Button data-testid={`remove-admin-${a.email}`} size="sm" variant="ghost" className="rounded-full h-8 text-destructive"
                        onClick={() => removeAdmin(a.email)}>Remove</Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="integrations" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="integrations-panel">
            <div className="rounded-2xl border border-border/60 bg-card p-6">
              <h3 className="font-outfit font-medium text-lg">SMTP (outgoing email)</h3>
              <p className="text-xs text-muted-foreground mt-1">
                When set, all platform email is sent via your SMTP server instead of the default provider.
                Port 587 (STARTTLS) is standard; port 465 (SSL) is used automatically as a fallback if the configured port fails.
                {integrations?.smtp?.host && <span className="text-emerald-500 block mt-1">Configured — emails go via {integrations.smtp.host}</span>}
              </p>
              <Button data-testid="smtp-test-btn" size="sm" variant="outline" className="rounded-full mt-3" onClick={testSmtp}>
                Send test email (uses saved settings)
              </Button>
              <div className="mt-4 space-y-3">
                <Input data-testid="smtp-host" placeholder="Host (e.g. smtp.mailgun.org)" value={smtpForm.host}
                  onChange={(e) => setSmtpForm({ ...smtpForm, host: e.target.value })} className="bg-background border-border/60" />
                <Input data-testid="smtp-port" type="number" placeholder="Port" value={smtpForm.port}
                  onChange={(e) => setSmtpForm({ ...smtpForm, port: Number(e.target.value) })} className="bg-background border-border/60" />
                <Input data-testid="smtp-username" placeholder="Username" value={smtpForm.username}
                  onChange={(e) => setSmtpForm({ ...smtpForm, username: e.target.value })} className="bg-background border-border/60" />
                <Input data-testid="smtp-password" type="password" placeholder={integrations?.smtp?.has_password ? "Password saved — enter to replace" : "Password"}
                  value={smtpForm.password} onChange={(e) => setSmtpForm({ ...smtpForm, password: e.target.value })}
                  className="bg-background border-border/60" />
                <Input data-testid="smtp-from" type="email" placeholder="From email (e.g. bookings@tablediary.app)" value={smtpForm.from_email}
                  onChange={(e) => setSmtpForm({ ...smtpForm, from_email: e.target.value })} className="bg-background border-border/60" />
                <Button data-testid="smtp-save-btn" onClick={() => saveIntegrations("smtp")} className="w-full rounded-full">Save SMTP</Button>
              </div>
            </div>
            <div className="rounded-2xl border border-border/60 bg-card p-6">
              <h3 className="font-outfit font-medium text-lg">Twilio (SMS notifications)</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Off by default. Once configured and enabled, add SMS to plans in the Plans tab.
                {integrations?.twilio?.account_sid && <span className="text-emerald-500 block mt-1">Configured — account {integrations.twilio.account_sid.slice(0, 8)}…</span>}
              </p>
              <div className="mt-4 space-y-3">
                <Input data-testid="twilio-sid" placeholder="Account SID" value={twilioForm.account_sid}
                  onChange={(e) => setTwilioForm({ ...twilioForm, account_sid: e.target.value })} className="bg-background border-border/60" />
                <Input data-testid="twilio-token" type="password" placeholder={integrations?.twilio?.has_token ? "Auth token saved — enter to replace" : "Auth token"}
                  value={twilioForm.auth_token} onChange={(e) => setTwilioForm({ ...twilioForm, auth_token: e.target.value })}
                  className="bg-background border-border/60" />
                <Input data-testid="twilio-from" placeholder="From number (e.g. +447...)" value={twilioForm.from_number}
                  onChange={(e) => setTwilioForm({ ...twilioForm, from_number: e.target.value })} className="bg-background border-border/60" />
                <div className="flex items-center justify-between">
                  <Label className="text-sm">SMS enabled</Label>
                  <Switch data-testid="twilio-enabled-switch" checked={twilioForm.enabled}
                    onCheckedChange={(v) => setTwilioForm({ ...twilioForm, enabled: v })} />
                </div>
                <Button data-testid="twilio-save-btn" onClick={() => saveIntegrations("twilio")} className="w-full rounded-full">Save Twilio</Button>
              </div>
            </div>
            <div className="rounded-2xl border border-border/60 bg-card p-6" data-testid="stripe-card">
              <h3 className="font-outfit font-medium text-lg">Stripe — live keys</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Powers subscription plans and the "Pay outstanding balance" button businesses see in their Billing tab.
                Sandbox (test) keys work out of the box; add your live keys here to take real payments.
              </p>
              <div className="mt-4 rounded-xl border border-border/40 bg-background p-4 text-sm">
                <p className="flex items-center justify-between">
                  <span className="text-muted-foreground">Mode</span>
                  <span data-testid="stripe-mode" className={`font-medium ${stripeCfg?.mode === "live" ? "text-emerald-500" : ""}`}>
                    {stripeCfg?.mode === "live" ? "Live" : "Sandbox (test)"}
                  </span>
                </p>
                <p className="flex items-center justify-between mt-2"><span className="text-muted-foreground">Currency</span><span className="font-medium">GBP £</span></p>
                {stripeCfg?.secret_key_set && (
                  <p className="flex items-center justify-between mt-2"><span className="text-muted-foreground">Live secret key</span><span className="font-medium">sk_…{stripeCfg.secret_key_hint}</span></p>
                )}
                {stripeCfg?.webhook_secret_set && (
                  <p className="flex items-center justify-between mt-2"><span className="text-muted-foreground">Webhook secret</span><span className="font-medium text-emerald-500">saved</span></p>
                )}
              </div>
              <div className="mt-4 space-y-3">
                <Input data-testid="stripe-publishable-key" placeholder="Publishable key (pk_live_…)" value={stripeForm.publishable_key}
                  onChange={(e) => setStripeForm({ ...stripeForm, publishable_key: e.target.value })} className="bg-background border-border/60" />
                <Input data-testid="stripe-secret-key" type="password" placeholder={stripeCfg?.secret_key_set ? "Secret key saved — enter to replace" : "Secret key (sk_live_…)"}
                  value={stripeForm.secret_key} onChange={(e) => setStripeForm({ ...stripeForm, secret_key: e.target.value })} className="bg-background border-border/60" />
                <Input data-testid="stripe-webhook-secret" type="password" placeholder={stripeCfg?.webhook_secret_set ? "Webhook secret saved — enter to replace" : "Webhook signing secret (whsec_…)"}
                  value={stripeForm.webhook_secret} onChange={(e) => setStripeForm({ ...stripeForm, webhook_secret: e.target.value })} className="bg-background border-border/60" />
                <div className="flex gap-2">
                  <Button data-testid="stripe-save-btn" onClick={saveStripeCfg} className="flex-1 rounded-full">Save live keys</Button>
                  {stripeCfg?.mode === "live" && (
                    <Button data-testid="stripe-clear-btn" variant="outline" className="rounded-full" onClick={clearStripeCfg}>Remove</Button>
                  )}
                </div>
              </div>
              {stripeCfg && (
                <div className="mt-5 rounded-xl border border-border/40 bg-background p-4" data-testid="stripe-webhook-guide">
                  <p className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Webhook setup (Stripe Dashboard → Developers → Webhooks)</p>
                  <ol className="text-xs text-muted-foreground mt-2 space-y-1.5 list-decimal list-inside">
                    <li>Add endpoint: <code className="text-foreground break-all">{stripeCfg.webhook_url}</code></li>
                    <li>Select these events to listen to:</li>
                  </ol>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {stripeCfg.webhook_events.map((ev) => (
                      <code key={ev} className="text-[10px] rounded-full border border-border/60 px-2 py-0.5 text-foreground">{ev}</code>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">3. Copy the endpoint's signing secret (whsec_…) into the field above and save.</p>
                </div>
              )}
            </div>
            <div className="rounded-2xl border border-border/60 bg-card p-6">
              <h3 className="font-outfit font-medium text-lg">GiftVouchers.app</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Connect your GiftVouchers.app account to sell and redeem gift vouchers. Settings storage only for now — checkout redemption comes later.
                {integrations?.giftvouchers?.base_url && <span className="text-emerald-500 block mt-1">Configured — {integrations.giftvouchers.base_url}</span>}
              </p>
              <div className="mt-4 space-y-3">
                <Input data-testid="gv-base-url" placeholder="Base URL (e.g. https://api.giftvouchers.app)" value={gvForm.base_url}
                  onChange={(e) => setGvForm({ ...gvForm, base_url: e.target.value })} className="bg-background border-border/60" />
                <Input data-testid="gv-api-key" type="password" placeholder={integrations?.giftvouchers?.has_key ? "API key saved — enter to replace" : "API key"}
                  value={gvForm.api_key} onChange={(e) => setGvForm({ ...gvForm, api_key: e.target.value })}
                  className="bg-background border-border/60" />
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Enabled</Label>
                  <Switch data-testid="gv-enabled-switch" checked={gvForm.enabled}
                    onCheckedChange={(v) => setGvForm({ ...gvForm, enabled: v })} />
                </div>
                <Button data-testid="gv-save-btn" onClick={() => saveIntegrations("giftvouchers")} className="w-full rounded-full">Save GiftVouchers</Button>
              </div>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="cms" className="mt-6">
          <div className="rounded-2xl border border-border/60 bg-card p-6 max-w-2xl" data-testid="cms-panel">
            <h3 className="font-outfit font-medium text-lg">Landing page content</h3>
            <p className="text-xs text-muted-foreground mt-1">Changes go live on the public site immediately after publishing.</p>
            {content && (
              <div className="mt-6 space-y-4">
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Announcement bar (leave empty to hide)</Label>
                  <Input data-testid="cms-announcement" value={content.announcement}
                    onChange={(e) => setContent({ ...content, announcement: e.target.value })}
                    className="mt-1.5 bg-background border-border/60" placeholder="e.g. Summer terrace season is here" />
                </div>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Hero eyebrow</Label>
                  <Input data-testid="cms-eyebrow" value={content.hero_eyebrow}
                    onChange={(e) => setContent({ ...content, hero_eyebrow: e.target.value })}
                    className="mt-1.5 bg-background border-border/60" />
                </div>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Hero title</Label>
                  <Input data-testid="cms-hero-title" value={content.hero_title}
                    onChange={(e) => setContent({ ...content, hero_title: e.target.value })}
                    className="mt-1.5 bg-background border-border/60" />
                </div>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Hero subtitle</Label>
                  <Textarea data-testid="cms-hero-subtitle" rows={2} value={content.hero_subtitle}
                    onChange={(e) => setContent({ ...content, hero_subtitle: e.target.value })}
                    className="mt-1.5 bg-background border-border/60" />
                </div>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Hero image URL</Label>
                  <Input data-testid="cms-hero-image" value={content.hero_image}
                    onChange={(e) => setContent({ ...content, hero_image: e.target.value })}
                    className="mt-1.5 bg-background border-border/60" placeholder="https://..." />
                </div>
                {site && (
                  <div className="border-t border-border/60 pt-5 mt-2 space-y-4">
                    <p className="text-xs tracking-[0.2em] uppercase text-primary">Site meta (SEO & browser)</p>
                    <div>
                      <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Page title</Label>
                      <Input data-testid="cms-site-title" value={site.site_title}
                        onChange={(e) => setSite({ ...site, site_title: e.target.value })}
                        className="mt-1.5 bg-background border-border/60" />
                    </div>
                    <div>
                      <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Meta description</Label>
                      <Textarea data-testid="cms-meta-description" rows={2} value={site.meta_description}
                        onChange={(e) => setSite({ ...site, meta_description: e.target.value })}
                        className="mt-1.5 bg-background border-border/60" />
                    </div>
                    <div>
                      <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Favicon</Label>
                      <div className="mt-1.5 flex gap-2 items-center">
                        <Input data-testid="cms-favicon" value={site.favicon_url}
                          onChange={(e) => setSite({ ...site, favicon_url: e.target.value })}
                          className="bg-background border-border/60" placeholder="https://.../favicon.png" />
                        <label data-testid="cms-favicon-upload"
                          className="shrink-0 text-xs px-3 py-2 rounded-full border border-border/60 text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                          {cmsUploading === "favicon" ? "Uploading…" : "Upload"}
                          <input type="file" accept="image/png,image/x-icon,image/svg+xml,image/jpeg,image/webp" className="hidden"
                            onChange={(e) => { e.target.files?.[0] && uploadAdminMedia("favicon", e.target.files[0], (url) => setSite((s) => ({ ...s, favicon_url: url }))); e.target.value = ""; }} />
                        </label>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Social share description</Label>
                      <Textarea data-testid="cms-share-description" rows={2} value={site.share_description || ""}
                        onChange={(e) => setSite({ ...site, share_description: e.target.value })}
                        className="mt-1.5 bg-background border-border/60" placeholder="Shown when the site is shared on socials (defaults to meta description)" />
                    </div>
                    <div>
                      <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Social share image</Label>
                      <div className="mt-1.5 flex gap-2 items-center">
                        <Input data-testid="cms-share-image" value={site.share_image_url || ""}
                          onChange={(e) => setSite({ ...site, share_image_url: e.target.value })}
                          className="bg-background border-border/60" placeholder="https://… (or upload)" />
                        <label data-testid="cms-share-image-upload"
                          className="shrink-0 text-xs px-3 py-2 rounded-full border border-border/60 text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                          {cmsUploading === "share" ? "Uploading…" : "Upload"}
                          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                            onChange={(e) => { e.target.files?.[0] && uploadAdminMedia("share", e.target.files[0], (url) => setSite((s) => ({ ...s, share_image_url: url }))); e.target.value = ""; }} />
                        </label>
                      </div>
                    </div>
                  </div>
                )}
                <Button data-testid="cms-save-btn" onClick={saveContent} className="rounded-full">Publish changes</Button>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-border/60 bg-card p-6 max-w-2xl mt-6" data-testid="cms-sections-panel">
            <h3 className="font-outfit font-medium text-lg">Homepage sections</h3>
            <p className="text-xs text-muted-foreground mt-1">Turn homepage sections on or off — changes go live when you publish.</p>
            <div className="mt-4 space-y-3">
              {[["hero", "Hero banner"], ["cities", "Discover by area"], ["tonight", "Tonight near you"], ["nearby", "Near you"], ["featured", "This week's tables"], ["pricing", "Pricing plans"], ["offers", "Offers strip"], ["cta", "Footer call-to-action"]].map(([k, label]) => (
                <div key={k} className="flex items-center justify-between">
                  <Label className="text-sm">{label}</Label>
                  <Switch data-testid={`cms-section-${k}`} checked={content?.sections?.[k] !== false}
                    onCheckedChange={(v) => setContent({ ...content, sections: { ...(content?.sections || {}), [k]: v } })} />
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-border/60 bg-card p-6 max-w-2xl mt-6" data-testid="cms-tour-panel">
            <h3 className="font-outfit font-medium text-lg">Product tour page</h3>
            <p className="text-xs text-muted-foreground mt-1">Content for the public /tour page. Leave an image URL unchanged to keep the default screenshot.</p>
            {tour && (
              <div className="mt-6 space-y-4">
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Hero eyebrow</Label>
                  <Input data-testid="cms-tour-eyebrow" value={tour.hero_eyebrow}
                    onChange={(e) => setTour({ ...tour, hero_eyebrow: e.target.value })}
                    className="mt-1.5 bg-background border-border/60" />
                </div>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Hero title</Label>
                  <Input data-testid="cms-tour-title" value={tour.hero_title}
                    onChange={(e) => setTour({ ...tour, hero_title: e.target.value })}
                    className="mt-1.5 bg-background border-border/60" />
                </div>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Hero subtitle</Label>
                  <Textarea data-testid="cms-tour-subtitle" rows={2} value={tour.hero_subtitle}
                    onChange={(e) => setTour({ ...tour, hero_subtitle: e.target.value })}
                    className="mt-1.5 bg-background border-border/60" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">CTA title</Label>
                    <Input data-testid="cms-tour-cta-title" value={tour.cta_title}
                      onChange={(e) => setTour({ ...tour, cta_title: e.target.value })}
                      className="mt-1.5 bg-background border-border/60" />
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">CTA subtitle</Label>
                    <Input data-testid="cms-tour-cta-subtitle" value={tour.cta_subtitle}
                      onChange={(e) => setTour({ ...tour, cta_subtitle: e.target.value })}
                      className="mt-1.5 bg-background border-border/60" />
                  </div>
                </div>
                <div className="border-t border-border/60 pt-5 mt-2 space-y-4">
                  <p className="text-xs tracking-[0.2em] uppercase text-primary">Tour sections</p>
                  {tour.features.map((f, i) => (
                    <div key={i} className="rounded-xl border border-border/40 p-4 space-y-3" data-testid={`tour-feature-edit-${i}`}>
                      <div className="flex items-center justify-between gap-3">
                        <Input value={f.tag} data-testid={`tour-feature-tag-${i}`}
                          onChange={(e) => setTour({ ...tour, features: tour.features.map((x, j) => j === i ? { ...x, tag: e.target.value } : x) })}
                          className="bg-background border-border/60 max-w-[200px]" />
                        <label className="flex items-center gap-2 text-xs text-muted-foreground shrink-0 cursor-pointer">
                          <input type="checkbox" checked={f.enabled !== false} data-testid={`tour-feature-enabled-${i}`}
                            onChange={(e) => setTour({ ...tour, features: tour.features.map((x, j) => j === i ? { ...x, enabled: e.target.checked } : x) })} />
                          Show section
                        </label>
                      </div>
                      <Input value={f.title} data-testid={`tour-feature-title-${i}`} placeholder="Section title"
                        onChange={(e) => setTour({ ...tour, features: tour.features.map((x, j) => j === i ? { ...x, title: e.target.value } : x) })}
                        className="bg-background border-border/60" />
                      <Textarea rows={2} value={f.body} data-testid={`tour-feature-body-${i}`} placeholder="Section text"
                        onChange={(e) => setTour({ ...tour, features: tour.features.map((x, j) => j === i ? { ...x, body: e.target.value } : x) })}
                        className="bg-background border-border/60" />
                      <Input value={f.image} data-testid={`tour-feature-image-${i}`} placeholder="/tour/calendar.png or https://…"
                        onChange={(e) => setTour({ ...tour, features: tour.features.map((x, j) => j === i ? { ...x, image: e.target.value } : x) })}
                        className="bg-background border-border/60" />
                    </div>
                  ))}
                </div>
                <Button data-testid="cms-tour-save-btn" onClick={saveContent} className="rounded-full">Publish changes</Button>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!customerEmail} onOpenChange={() => setCustomerEmail(null)}>
        <DialogContent className="bg-card border-border/60 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-outfit">Bookings — {customerEmail}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2" data-testid="customer-bookings-list">
            {customerBookings.map((b) => (
              <div key={b.id} data-testid={`customer-booking-${b.id}`}
                className="rounded-xl border border-border/40 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">{b.restaurant_name} · {b.date} {b.time}</p>
                  <p className="text-xs text-muted-foreground">
                    Party of {b.party_size} · Table {b.table_label}{b.dietary ? ` · Dietary: ${b.dietary}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{b.status.replace("_", " ")}</Badge>
                  <Select value="" onValueChange={(v) => overrideBooking(b, v)}>
                    <SelectTrigger data-testid={`override-status-${b.id}`} className="w-28 h-8 bg-background border-border/60 text-xs">
                      <SelectValue placeholder="Override" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="seated">Seated</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="no_show">No show</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
            {customerBookings.length === 0 && <p className="text-sm text-muted-foreground">No bookings for this customer.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
